import type { NormalizedMarketRow } from '@/types/market';
import type { BenchmarkStats, ScoreBand } from '@/types/insights';
import { clamp, zScore } from '@/utils/numbers';
import { scoringWeights } from '@/config/scoringWeights';
import { temperatureBand } from './bands';

/**
 * hotColdScore: the headline "temperature" of a geography relative to the
 * rest of the covered map right now. Composite of four z-scored raw
 * metrics -- days on market, months of inventory, sale-to-list ratio, and
 * price-reduction share -- each weighted per scoringWeights.hotCold.
 * Range: -100 (coldest) .. +100 (hottest).
 */
export function computeHotColdScore(row: NormalizedMarketRow, benchmark: BenchmarkStats): ScoreBand {
  const w = scoringWeights.hotCold;

  const zDom = zScore(row.medianDaysOnMarket, benchmark.meanByField.medianDaysOnMarket, benchmark.stdByField.medianDaysOnMarket);
  const zMoi = zScore(row.monthsOfInventory, benchmark.meanByField.monthsOfInventory, benchmark.stdByField.monthsOfInventory);
  const zStl = zScore(row.saleToListRatioPct, benchmark.meanByField.saleToListRatioPct, benchmark.stdByField.saleToListRatioPct);
  const zCut = zScore(
    row.priceReductionSharePct,
    benchmark.meanByField.priceReductionSharePct,
    benchmark.stdByField.priceReductionSharePct,
  );

  const parts: number[] = [];
  if (zDom !== undefined) parts.push(zDom * w.domZWeight);
  if (zMoi !== undefined) parts.push(zMoi * w.moiZWeight);
  if (zStl !== undefined) parts.push(zStl * w.saleToListZWeight);
  if (zCut !== undefined) parts.push(zCut * w.priceCutZWeight);

  if (parts.length === 0) return temperatureBand(undefined);

  const composite = parts.reduce((a, b) => a + b, 0);
  const scaled = clamp(composite * w.scaleToRange, -100, 100);
  return temperatureBand(scaled);
}
